/**
 * PORT-005 §6 — **changer d'espace, d'une main.**
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 CE QUI MANQUAIT, ET CE N'ÉTAIT PAS UN PADDING
 * ═══════════════════════════════════════════════════════════════════════════
 * La revue mobile du 2026-09-10 a d'abord reproché 32 px aux entrées de ce
 * menu. Le vrai constat est un cran au-dessus : **ce n'était pas le bon
 * composant**. `PORT-005 §6` écrit « `DropdownMenu` (**desktop**),
 * `Sheet side="bottom"` **sous `md:`** via `useIsMobile()`, `Check` sur l'entrée
 * active », et `PORT-008 §8.6` confirme que « le **volet bas est réservé au
 * sélecteur d'espace** ». L'implémentation rendait un menu déroulant de bureau
 * à **toutes** les largeurs, inliné dans l'en-tête : pas de volet bas, pas de
 * `Check`, pas d'icône de type. `grep PortalContextSwitcher` rendait **0** — le
 * composant que la spec nomme n'existait pas.
 *
 * La question du PO était « choisit-on son espace d'une main ? ». Dans un menu
 * ancré en haut d'écran, la réponse est non : la liste s'ouvre là où le pouce
 * n'atteint pas, sur la surface même où l'apprenant et l'intervenant passent
 * leur temps. Le volet bas règle le geste **et** les zones sûres.
 *
 * ⛔ **Aucun `p-*` sur le `SheetContent`.** La variante `bottom` de
 * `ui/sheet.tsx` est la SEULE du dépôt à porter ses quatre zones sûres, et
 * tailwind-merge les efface au premier `p-0` / `p-4` d'appelant. Un padding
 * posé ici rendrait le volet muet sur la barre d'accueil — après quoi on
 * cherchera pourquoi la dernière entrée n'est pas cliquable.
 *
 * ⚠️ **Un seul décideur de palier** : `useIsMobile()`, comme la coque et comme
 * les 55 autres fichiers du produit (dont `DataTable`, qui fait basculer toutes
 * les tables en tuiles). Une classe `md:hidden` ne saurait pas exprimer le
 * second cas du hook — le téléphone en PAYSAGE — et rendrait un menu de bureau
 * à l'intervenant qui tourne son téléphone entre deux salles.
 */

import { forwardRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import {
    cleDuTypeDEspace,
    derniereVisiteDuContexte,
    iconeDuTypeDEspace,
    libelleDeLEspaceEnCours,
    ligneDeuxDuContexte,
} from '@/components/portal/contextKind'
import { LigneDeuxDeLEspace } from '@/components/portal/LigneDeuxDeLEspace'
import { ordonnerLesEspaces } from '@/components/portal/ordreDesEspaces'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import type { PortalContext } from '@/hooks/usePortalMe.types'

interface PortalContextSwitcherProps {
    contextes: PortalContext[]
    contexteActif: PortalContext | null
    /** Rend `true` quand l'espace est ouvert — voir `useChoisirLEspace`. */
    onChoisir: (grantId: string) => Promise<boolean>
}

/**
 * Le déclencheur, identique aux deux paliers.
 *
 * ⚠️ `min-h-11` puis `md:min-h-0 md:h-auto` : un `h-auto` seul EFFAÇAIT le
 * `h-10` du socle par tailwind-merge et la cible retombait à 28 px, sous le
 * plancher de 32 px du §13.2 — sur la seule action de l'en-tête au téléphone.
 * Le gardien de plancher ne sait refuser qu'une hauteur écrite trop petite,
 * jamais une hauteur effacée.
 */
const Declencheur = forwardRef<
    HTMLButtonElement,
    { libelle: string } & React.ComponentPropsWithoutRef<'button'>
>(function Declencheur({ libelle, className, ...reste }, ref) {
    /**
     * 🔴 **`forwardRef` et `...reste` ne sont pas du zèle : sans eux le volet ne
     * s'ouvre JAMAIS.**
     *
     * `SheetTrigger asChild` et `DropdownMenuTrigger asChild` ne rendent aucune
     * balise : ils CLONENT leur enfant en y injectant `onClick`,
     * `aria-haspopup`, `aria-expanded`, `aria-controls` et une `ref`. Un
     * composant de fonction qui ne lit que `libelle` les jette tous
     * silencieusement — le bouton s'affiche, il est joli, et il ne fait rien.
     *
     * ⚠️ Aucune erreur, aucun avertissement : c'est le gardien
     * `laCoqueSeNavigueAuTelephone` qui l'a trouvé, en demandant
     * `aria-haspopup` au déclencheur et en recevant `null`. Une revue à l'œil
     * ne l'aurait pas vu.
     */
    return (
        <Button
            ref={ref}
            variant="ghost"
            className={cn('min-h-11 max-w-full justify-start px-2 py-1 md:min-h-0 md:h-auto', className)}
            {...reste}
        >
            <span className="truncate font-medium">{libelle}</span>
            <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-60" aria-hidden />
        </Button>
    )
})

/**
 * Lignes 2 et 3 d'une entrée — les MÊMES aux deux paliers, et les mêmes que la page
 * de choix (`contextKind.ts`) : le régime d'une structure (arbitrage n° 11) et
 * « Dernière visite », que le wireframe §5.1 place dans ce menu (décision n° 5) —
 * sauf sous l'espace en cours (A19 : `derniereVisiteDuContexte` y rend `null`).
 */
function LignesDeLEspace({ contexte, actifId }: { contexte: PortalContext; actifId: string | null }) {
    const { t } = useTranslation()
    const ligne2 = ligneDeuxDuContexte(contexte, t)
    const visite = derniereVisiteDuContexte(contexte, actifId, t)
    return (
        <>
            {/* E8 (08/10) — jamais coupée : ce qui départage deux homonymes s'écrit à sa fin. */}
            {ligne2 && <LigneDeuxDeLEspace texte={ligne2} />}
            {visite && <span className="block truncate text-xs text-muted-foreground">{visite}</span>}
        </>
    )
}

/** Une entrée du volet bas : 56 px, l'icône de sa nature, et `Check` si active. */
function EntreeDuVolet({
    contexte,
    actifId,
    onChoisir,
}: {
    contexte: PortalContext
    actifId: string | null
    onChoisir: () => void
}) {
    const { t } = useTranslation()
    const actif = contexte.id === actifId
    const Icone = iconeDuTypeDEspace(contexte.kind)
    return (
        <button
            type="button"
            onClick={onChoisir}
            aria-current={actif ? 'true' : undefined}
            // 56 px et non 44 : deux lignes de texte y tiennent, et c'est un
            // geste qu'on fait au pouce en marchant.
            className={cn(
                'flex min-h-14 w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                actif ? 'bg-accent text-accent-foreground' : 'hover:bg-accent hover:text-accent-foreground',
            )}
        >
            <Icone className="h-5 w-5 shrink-0 opacity-70" aria-hidden />
            <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">
                    {t(cleDuTypeDEspace(contexte.kind))}
                </span>
                <LignesDeLEspace contexte={contexte} actifId={actifId} />
            </span>
            {/* ⛔ `Check` ET `aria-current` : l'un pour l'œil, l'autre pour le
                lecteur d'écran. Une coche seule laisserait un aveugle sans
                moyen de savoir où il est. */}
            {actif && <Check className="h-4 w-4 shrink-0" aria-hidden />}
        </button>
    )
}

export function PortalContextSwitcher({
    contextes,
    contexteActif,
    onChoisir,
}: PortalContextSwitcherProps) {
    const { t, i18n } = useTranslation()
    const estMobile = useIsMobile() === true
    const [ouvert, setOuvert] = useState(false)
    const libelle = libelleDeLEspaceEnCours(contexteActif, contextes.length, t)
    const actifId = contexteActif?.id ?? null
    // PORT-005 §5.2 — le MÊME ordre que la page de choix (`ordreDesEspaces.ts`).
    const espaces = ordonnerLesEspaces(contextes, actifId, t, i18n.language)

    if (estMobile) {
        return (
            <Sheet open={ouvert} onOpenChange={setOuvert}>
                <SheetTrigger asChild>
                    <Declencheur libelle={libelle} />
                </SheetTrigger>
                {/* ⛔ Pas de `p-*` ici — voir l'en-tête. `max-h` et défilement :
                    quelqu'un peut avoir plus d'espaces que l'écran n'a de
                    place, et un volet qui déborde perd ses dernières entrées
                    sous la barre d'accueil. */}
                <SheetContent side="bottom" className="max-h-[80svh] overflow-y-auto">
                    <SheetHeader className="text-left">
                        <SheetTitle className="text-base">{t('portal.context.title')}</SheetTitle>
                    </SheetHeader>
                    <ul className="mt-2 space-y-1">
                        {espaces.map((c) => (
                            <li key={c.id}>
                                <EntreeDuVolet
                                    contexte={c}
                                    actifId={actifId}
                                    // 🔴 Fermer APRÈS, et seulement sur succès
                                    // (FE-8) : fermé avant l'appel, le volet
                                    // disparaissait et un refus ne laissait
                                    // rien. Resté ouvert, il montre la liste
                                    // relue — l'espace fermé n'y est plus.
                                    onChoisir={() => {
                                        void onChoisir(c.id).then((ouvert) => {
                                            if (ouvert) setOuvert(false)
                                        })
                                    }}
                                />
                            </li>
                        ))}
                    </ul>
                </SheetContent>
            </Sheet>
        )
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Declencheur libelle={libelle} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-w-[min(20rem,90vw)]">
                {/* La clé existait en FR et en EN et n'était utilisée NULLE PART.
                    Sans elle, un menu qui liste « Espace client / Espace
                    apprenant » ne dit pas à quoi il sert. */}
                <DropdownMenuLabel>{t('portal.context.title')}</DropdownMenuLabel>
                {espaces.map((c) => {
                    const Icone = iconeDuTypeDEspace(c.kind)
                    const actif = c.id === actifId
                    return (
                        <DropdownMenuItem
                            key={c.id}
                            onSelect={() => void onChoisir(c.id)}
                            aria-current={actif ? 'true' : undefined}
                            className="min-h-11 items-start gap-2 md:min-h-0"
                        >
                            <Icone className="mt-0.5 h-4 w-4 shrink-0 opacity-70" aria-hidden />
                            <span className="min-w-0 flex-1">
                                <span className="block truncate font-medium">
                                    {t(cleDuTypeDEspace(c.kind))}
                                </span>
                                <LignesDeLEspace contexte={c} actifId={actifId} />
                            </span>
                            {actif && <Check className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />}
                        </DropdownMenuItem>
                    )
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
